/**
 * bankImport/parseCamt053 — ISO-20022-CAMT.053-Parser (v02 UND v08),
 * architecture-bank-reconciliation.md §5.4/§9.1.
 *
 * **CAMT.052-Unterstützung (BR-15):** Manche Banken liefern statt eines
 * vollen `BkToCstmrStmt`-Kontoauszugs (camt.053, „Statement") einen
 * `BkToCstmrAcctRpt`-Container (camt.052, „Account Report" — z.B. Tages-/
 * Zwischen-Umsatzanzeigen). Beide Wrapper sind strukturell identisch
 * (`Acct`/`Bal`/`Ntry`-Substruktur ist in camt.052 und camt.053 gleich
 * aufgebaut, nur das enthaltende Element heißt `Rpt` statt `Stmt`), daher
 * verarbeitet dieser Parser BEIDE ohne separaten `parseCamt052`-Export:
 * es wird zuerst nach `BkToCstmrStmt`/`Stmt`, sonst nach `BkToCstmrAcctRpt`/
 * `Rpt` gesucht. Der bestehende `Sts==='BOOK'`-Filter überspringt in camt.052
 * enthaltene `PDNG`-Buchungen (Pending) unverändert korrekt.
 *
 * Namespace-agnostisch (per `localName`-Vergleich statt `tagName`), da
 * `…053.001.02` und `…053.001.08` unterschiedliche Namespaces UND teils
 * unterschiedliche Element-Namen verwenden (z.B. `BIC` vs. `BICFI`).
 *
 * DOMParser-Falle (§5.4): `DOMParser.parseFromString` wirft bei kaputtem XML
 * NICHT — es liefert ein Dokument mit einem `<parsererror>`-Element. Dieser
 * Parser prüft das EXPLIZIT und wirft `BankImportError('xml_invalid')`.
 *
 * Interpretations-Entscheidung „0-Umsätze" vs. `no_transactions` (v1, siehe
 * changelog-parser.md — Architektur legt das Verhalten nicht abschließend
 * fest): Eine Datei mit `Stmt`, aber OHNE gebuchte `Ntry`-Elemente ist ein
 * valider „0 Umsätze"-Auszug → `rows: []`, KEIN Fehler (der User bekommt
 * z.B. trotzdem die Salden importiert). `BankImportError('no_transactions')`
 * wird nur geworfen, wenn die Datei strukturell GAR KEIN `Stmt`-Element
 * enthält (nichts, das importiert werden könnte). Fehlt sogar
 * `BkToCstmrStmt`, ist das ein `xml_invalid` (falsche Struktur, obwohl
 * `detectStatementFormat` camt053 vermutet hatte).
 *
 * Multi-`TxDtls`-Sammler (§5.4): `TxDtls` MIT eigenem `Amt` → eine Zeile pro
 * `TxDtls`; `TxDtls` OHNE `Amt` (keine Beträge auf Transaktionsebene) → EINE
 * Zeile aus der `Ntry`-Ebene (Fallback), Gegenpartei-Infos ggf. aus dem
 * ersten `TxDtls`, falls vorhanden.
 *
 * Zeilen-Invariante (Härtung v2, s. changelog-parser.md): jede gebaute Zeile
 * durchläuft `assertValidParsedStatementRow` (`direction`⟺Vorzeichen,
 * §6.2), bevor sie als gültig gilt. Eine gebuchte `Ntry` mit `Amt` „0,00"
 * (z.B. reine Info-/Storno-Buchung) hätte sonst — abhängig vom `CdtDbtInd`
 * — als valide `credit`/`debit`-Zeile mit `amountCents: 0` durchgerutscht;
 * jetzt landet sie als Zeilenfehler in `ParsedStatement.errors[]`, analog
 * zur bestehenden 0,00-Behandlung in `parseBankCsv`.
 *
 * @module bankImport/parseCamt053
 */

import { centsFromDecimalString } from './money';
import {
  assertValidParsedStatementRow,
  BankImportError,
  type ParsedStatement,
  type ParsedStatementRow,
} from './types';

// ─────────────────────────────────────────────────────────────────────────
// Namespace-agnostische DOM-Helfer
// ─────────────────────────────────────────────────────────────────────────

function directChildren(el: Element, localName: string): Element[] {
  const result: Element[] = [];
  for (let i = 0; i < el.children.length; i += 1) {
    const child = el.children[i];
    if (child.localName === localName || child.tagName === localName) {
      result.push(child);
    }
  }
  return result;
}

function directChild(el: Element | null, localName: string): Element | null {
  if (!el) return null;
  const children = directChildren(el, localName);
  return children.length > 0 ? children[0] : null;
}

function textOf(el: Element | null): string | null {
  if (!el) return null;
  const text = el.textContent;
  return text !== null && text.trim().length > 0 ? text.trim() : null;
}

function serializeFragment(el: Element): string {
  try {
    return new XMLSerializer().serializeToString(el).slice(0, 2000);
  } catch {
    return '';
  }
}

/** Uppercase ohne Spaces — lokal dupliziert, s. Begründung in `dedup.ts` (keine bankMatching-Abhängigkeit). */
function normalizeIbanLocal(raw: string | null): string | null {
  if (!raw) return null;
  const cleaned = raw.toUpperCase().replace(/\s+/g, '');
  return cleaned.length > 0 ? cleaned : null;
}

// ─────────────────────────────────────────────────────────────────────────
// Datums-/Betrags-Extraktion
// ─────────────────────────────────────────────────────────────────────────

/** `<Dt><Dt>YYYY-MM-DD</Dt></Dt>` ODER `<Dt><DtTm>YYYY-MM-DDThh:mm:ss</DtTm></Dt>`. */
function extractDate(dtWrapper: Element | null): string | null {
  if (!dtWrapper) return null;
  const dateOnly = textOf(directChild(dtWrapper, 'Dt'));
  if (dateOnly) return dateOnly.slice(0, 10);
  const dateTime = textOf(directChild(dtWrapper, 'DtTm'));
  return dateTime ? dateTime.slice(0, 10) : null;
}

function balanceAmountCents(bal: Element): number | null {
  const amtEl = directChild(bal, 'Amt');
  const raw = textOf(amtEl);
  if (raw === null) return null;
  try {
    const magnitude = Math.abs(centsFromDecimalString(raw, 'en'));
    const cdtDbt = textOf(directChild(bal, 'CdtDbtInd'));
    return cdtDbt === 'DBIT' ? -magnitude : magnitude;
  } catch {
    return null;
  }
}

/**
 * Konto-Identifikation aus einem `Id`-Element: primär `Id/IBAN`, Fallback
 * `Id/Othr/Id` (BR-16 #7). Nicht-SEPA-/Fremd-/manche Geschäftskonten tragen
 * KEIN `IBAN`-Element, sondern eine proprietäre Kontonummer unter `Othr/Id`.
 *
 * Schema-Entscheidung (§7 BR-16): der Wert bleibt im bestehenden
 * `accountIban`/`counterpartyIban`-Feld (KEIN neues Feld, Schema additiv
 * stabil). Eine generische Konto-Id ist für die Wizard-Konto-Zuordnung besser
 * als `null`; die IBAN-Normalisierung (Uppercase, Space-Strip) ist für eine
 * reine Kontonummer harmlos. Bei vorhandenem `IBAN` ist das Verhalten
 * unverändert (IBAN hat Vorrang) — bestehende Fixtures bleiben grün.
 */
function extractAccountIdOrIban(idEl: Element | null): string | null {
  const iban = normalizeIbanLocal(textOf(directChild(idEl, 'IBAN')));
  if (iban) return iban;
  const othr = directChild(idEl, 'Othr');
  return normalizeIbanLocal(textOf(directChild(othr, 'Id')));
}

function extractAccountIban(stmt: Element): string | null {
  const acct = directChild(stmt, 'Acct');
  const id = directChild(acct, 'Id');
  return extractAccountIdOrIban(id);
}

function extractBalances(stmt: Element): {
  openingBalanceCents: number | null;
  closingBalanceCents: number | null;
  closingBalanceDate: string | null;
} {
  let openingBalanceCents: number | null = null;
  let closingBalanceCents: number | null = null;
  let closingBalanceDate: string | null = null;

  for (const bal of directChildren(stmt, 'Bal')) {
    const tp = directChild(bal, 'Tp');
    const cdOrPrtry = directChild(tp, 'CdOrPrtry');
    const code = textOf(directChild(cdOrPrtry, 'Cd'));

    if (code === 'OPBD') {
      openingBalanceCents = balanceAmountCents(bal);
    } else if (code === 'CLBD') {
      closingBalanceCents = balanceAmountCents(bal);
      closingBalanceDate = extractDate(directChild(bal, 'Dt'));
    }
  }

  return { openingBalanceCents, closingBalanceCents, closingBalanceDate };
}

/**
 * MA-2 (architecture-mahn-automatik.md §6.3/§4.2): `Stmt/FrToDt` trägt die
 * echte, vom kontoführenden Institut bestätigte Auszugsperiode — anders als
 * `statementFrom/To` (§6.3 `bankApi.ts`), das nur `min/max(bookingDate)` der
 * IN DER DATEI enthaltenen Buchungen ist und bei umsatzarmen Randtagen
 * künstliche Lücken vortäuscht. `FrToDt` ist ISO-20022-OPTIONAL (0..1) — fehlt
 * es, liefert diese Funktion `{ coverageFrom: null, coverageTo: null }`
 * (fail-closed: der Aufrufer fällt dann auf `statementFrom/To` zurück, §6.3).
 * Struktur `<FrToDt><FrDtTm>…</FrDtTm><ToDtTm>…</ToDtTm></FrToDt>` — beide
 * Kind-Elemente sind laut Schema stets `DtTm` (volles ISO-8601-DateTime),
 * nicht das `Dt`-Wrapper-Muster von `extractDate` (`<Dt><Dt>…</Dt></Dt>`).
 */
function extractCoveragePeriod(stmt: Element): { coverageFrom: string | null; coverageTo: string | null } {
  const frToDt = directChild(stmt, 'FrToDt');
  if (!frToDt) return { coverageFrom: null, coverageTo: null };
  const fr = textOf(directChild(frToDt, 'FrDtTm'));
  const to = textOf(directChild(frToDt, 'ToDtTm'));
  return {
    coverageFrom: fr ? fr.slice(0, 10) : null,
    coverageTo: to ? to.slice(0, 10) : null,
  };
}

function extractBankTxCode(ntry: Element): string | null {
  const bkTxCd = directChild(ntry, 'BkTxCd');
  const domn = directChild(bkTxCd, 'Domn');
  const domnCd = textOf(directChild(domn, 'Cd'));
  const fmly = directChild(domn, 'Fmly');
  const fmlyCd = textOf(directChild(fmly, 'Cd'));
  if (!domnCd && !fmlyCd) return null;
  return [domnCd, fmlyCd].filter((v): v is string => !!v).join('/');
}

/** `RltdPties/CdtrSchmeId/…/Id` — tiefste `Id` mit Text (PrvtId/Othr/Id oder OrgId/Othr/Id). */
function extractCreditorId(txDtls: Element): string | null {
  const cdtrSchmeIdEls = txDtls.getElementsByTagNameNS('*', 'CdtrSchmeId');
  if (cdtrSchmeIdEls.length === 0) return null;
  const idEls = cdtrSchmeIdEls[0].getElementsByTagNameNS('*', 'Id');
  for (let i = idEls.length - 1; i >= 0; i -= 1) {
    const text = textOf(idEls[i]);
    if (text) return text;
  }
  return null;
}

/**
 * Richtungslogik §5.4: bei `CRDT` ist die Gegenpartei `Dbtr`/`DbtrAcct`,
 * bei `DBIT` ist sie `Cdtr`/`CdtrAcct`. BIC-Element-Name unterscheidet sich
 * zwischen v02 (`BIC`) und v08 (`BICFI`) — beide werden versucht.
 */
function extractCounterparty(
  partySource: Element | null,
  direction: 'credit' | 'debit'
): { counterpartyName: string | null; counterpartyIban: string | null; counterpartyBic: string | null } {
  if (!partySource) {
    return { counterpartyName: null, counterpartyIban: null, counterpartyBic: null };
  }
  const rltdPties = directChild(partySource, 'RltdPties');
  const partyTag = direction === 'credit' ? 'Dbtr' : 'Cdtr';
  const acctTag = direction === 'credit' ? 'DbtrAcct' : 'CdtrAcct';

  const partyEl = directChild(rltdPties, partyTag);
  // BR-16 #1: v08 (`Party40Choice`) verschachtelt den Namen unter `<Pty>`
  // (`RltdPties/Dbtr/Pty/Nm`), v02 (`PartyIdentification32`) trägt `<Nm>` direkt
  // unter `Dbtr`/`Cdtr`. Reale Sparkasse-v08-Dateien nutzen die `Pty`-Form; die
  // alten „v08"-Fixtures nutzten zufällig die flache v02-Form und maskierten das.
  // Zweistufig: direktes `Nm` (v02) hat Vorrang, sonst `Pty/Nm` (v08).
  const counterpartyName =
    textOf(directChild(partyEl, 'Nm')) ?? textOf(directChild(directChild(partyEl, 'Pty'), 'Nm'));

  const acctEl = directChild(rltdPties, acctTag);
  const acctId = directChild(acctEl, 'Id');
  // BR-16 #7: IBAN mit Fallback auf `Othr/Id` (Nicht-SEPA-/Fremdkonten).
  const counterpartyIban = extractAccountIdOrIban(acctId);

  const rltdAgts = directChild(partySource, 'RltdAgts');
  const agtTag = direction === 'credit' ? 'DbtrAgt' : 'CdtrAgt';
  const agtEl = directChild(rltdAgts, agtTag);
  const finInstnId = directChild(agtEl, 'FinInstnId');
  const counterpartyBic = textOf(directChild(finInstnId, 'BIC')) ?? textOf(directChild(finInstnId, 'BICFI'));

  return { counterpartyName, counterpartyIban, counterpartyBic };
}

/**
 * Verwendungszweck-Extraktion (BR-16 #4). Reihenfolge (dokumentiert, weil sie
 * die Match-Qualität bestimmt):
 *  1. `RmtInf/Ustrd[]` — unstrukturierter Freitext (bisheriges Verhalten, Vorrang).
 *  2. `RmtInf/Strd/CdtrRefInf/Ref` (ISO-11649 `RF…`-Rechnungsreferenz) +
 *     `RmtInf/Strd/AddtlRmtInf` — strukturierte Remittance. Viele
 *     Geschäftszahlungen tragen die Rechnungsref NUR hier, nicht im `Ustrd`.
 *  3. `Ntry/AddtlNtryInf` (Sparkasse-typischer Ntry-Freitext) — NUR als letzter
 *     Fallback, wenn `RmtInf` gar nichts lieferte (via `addtlNtryInf`-Param,
 *     da dieses Feld auf Ntry-Ebene und nicht im `partySource`/TxDtls liegt).
 *
 * Teile werden mit `' '` konkateniert. Substring-Dedup verhindert Doppeltext,
 * wenn `Ustrd` die strukturierte Ref bereits im Klartext enthält (häufig bei
 * `RF…`-Referenzen, die zusätzlich im Ustrd stehen).
 */
function extractPurpose(partySource: Element | null, addtlNtryInf: string | null): string {
  const parts: string[] = [];

  const rmtInf = partySource ? directChild(partySource, 'RmtInf') : null;
  if (rmtInf) {
    for (const ustrd of directChildren(rmtInf, 'Ustrd')) {
      const text = textOf(ustrd);
      if (text) parts.push(text);
    }
    for (const strd of directChildren(rmtInf, 'Strd')) {
      const ref = textOf(directChild(directChild(strd, 'CdtrRefInf'), 'Ref'));
      if (ref) parts.push(ref);
      for (const addtl of directChildren(strd, 'AddtlRmtInf')) {
        const text = textOf(addtl);
        if (text) parts.push(text);
      }
    }
  }

  // Letzter Fallback: Ntry-Freitext nur, wenn RmtInf leer blieb.
  if (parts.length === 0 && addtlNtryInf) {
    parts.push(addtlNtryInf);
  }

  const deduped: string[] = [];
  for (const part of parts) {
    if (!deduped.some((existing) => existing.includes(part))) {
      deduped.push(part);
    }
  }
  return deduped.join(' ');
}

// ─────────────────────────────────────────────────────────────────────────
// Zeilen-Aufbau
// ─────────────────────────────────────────────────────────────────────────

interface RowCommonFields {
  bookgDt: string;
  valDt: string | null;
  acctSvcrRef: string | null;
  bankTxCode: string | null;
  /** Ntry-Ebene `AddtlNtryInf` — Purpose-Fallback (BR-16 #4), da nicht im TxDtls. */
  addtlNtryInf: string | null;
}

function buildRow(args: {
  common: RowCommonFields;
  amountRaw: string;
  currency: string;
  cdtDbtInd: string | null;
  partySource: Element | null;
  rawSourceEl: Element;
}): ParsedStatementRow {
  const magnitude = Math.abs(centsFromDecimalString(args.amountRaw, 'en'));
  if (args.cdtDbtInd !== 'CRDT' && args.cdtDbtInd !== 'DBIT') {
    throw new Error(`Ntry/TxDtls ohne gültiges CdtDbtInd: "${args.cdtDbtInd}".`);
  }
  const direction: 'credit' | 'debit' = args.cdtDbtInd === 'DBIT' ? 'debit' : 'credit';
  const amountCents = direction === 'debit' ? -magnitude : magnitude;

  const { counterpartyName, counterpartyIban, counterpartyBic } = extractCounterparty(args.partySource, direction);

  const refs = directChild(args.partySource, 'Refs');
  const endToEndId = textOf(directChild(refs, 'EndToEndId'));
  const mandateRef = textOf(directChild(refs, 'MndtId'));
  const creditorId = args.partySource ? extractCreditorId(args.partySource) : null;
  const purpose = extractPurpose(args.partySource, args.common.addtlNtryInf);

  return {
    bookingDate: args.common.bookgDt,
    valueDate: args.common.valDt,
    amountCents,
    direction,
    currency: args.currency,
    counterpartyName,
    counterpartyIban,
    counterpartyBic,
    purpose,
    endToEndId,
    mandateRef,
    creditorId,
    bankTxCode: args.common.bankTxCode,
    acctSvcrRef: args.common.acctSvcrRef,
    rawSource: serializeFragment(args.rawSourceEl),
  };
}

function extractEntryRows(ntry: Element): {
  rows: ParsedStatementRow[];
  errors: Array<{ rawLine: string; reason: string }>;
} {
  const rows: ParsedStatementRow[] = [];
  const errors: Array<{ rawLine: string; reason: string }> = [];

  const valDt = extractDate(directChild(ntry, 'ValDt'));
  // BR-16 #3: `BookgDt` ist ISO 0..1 (optional); Interim-/camt.052-Anzeigen
  // tragen teils nur `ValDt`. Fehlt `BookgDt`, wird die Valuta als
  // Buchungsdatum genutzt (übliche, akzeptable Näherung). Nur wenn BEIDE
  // fehlen → Zeilenfehler (statt vorher: fehlendes BookgDt allein = Fehler).
  const bookgDt = extractDate(directChild(ntry, 'BookgDt')) ?? valDt;
  if (!bookgDt) {
    errors.push({
      rawLine: serializeFragment(ntry),
      reason: 'Ntry ohne BookgDt und ohne ValDt — Zeile übersprungen.',
    });
    return { rows, errors };
  }
  const acctSvcrRef = textOf(directChild(ntry, 'AcctSvcrRef'));
  const bankTxCode = extractBankTxCode(ntry);
  const addtlNtryInf = textOf(directChild(ntry, 'AddtlNtryInf'));
  const common: RowCommonFields = { bookgDt, valDt, acctSvcrRef, bankTxCode, addtlNtryInf };

  const ntryDtls = directChild(ntry, 'NtryDtls');
  const txDtlsList = ntryDtls ? directChildren(ntryDtls, 'TxDtls') : [];
  const txDtlsWithAmounts = txDtlsList.filter((tx) => directChild(tx, 'Amt') !== null);

  if (txDtlsWithAmounts.length > 0) {
    // Sammler-Pfad: TxDtls MIT eigenem Betrag → eine Zeile pro TxDtls.
    for (const tx of txDtlsWithAmounts) {
      const amtEl = directChild(tx, 'Amt');
      const amountRaw = textOf(amtEl) ?? '';
      const currency = amtEl?.getAttribute('Ccy') ?? 'EUR';
      // Money-Correctness-Fix (2026-07-09): ISO 20022 macht CdtDbtInd auf
      // Ntry-Ebene PFLICHT, auf TxDtls-Ebene OPTIONAL. Reale Bank-Dateien
      // (z.B. Sparkasse camt.052.001.08) tragen CdtDbtInd NUR auf Ntry,
      // obwohl der Betrag im TxDtls steht (Sammler-Pfad greift wegen Amt).
      // TxDtls-Wert hat Vorrang, falls vorhanden — sonst Fallback auf Ntry.
      const cdtDbtInd = textOf(directChild(tx, 'CdtDbtInd')) ?? textOf(directChild(ntry, 'CdtDbtInd'));
      try {
        const row = buildRow({ common, amountRaw, currency, cdtDbtInd, partySource: tx, rawSourceEl: tx });
        assertValidParsedStatementRow(row);
        rows.push(row);
      } catch (err) {
        errors.push({ rawLine: serializeFragment(tx), reason: (err as Error).message });
      }
    }
    return { rows, errors };
  }

  // Fallback-Pfad: keine Beträge auf TxDtls-Ebene → eine Zeile aus der Ntry-Ebene.
  const ntryAmtEl = directChild(ntry, 'Amt');
  const amountRaw = textOf(ntryAmtEl) ?? '';
  const currency = ntryAmtEl?.getAttribute('Ccy') ?? 'EUR';
  const cdtDbtInd = textOf(directChild(ntry, 'CdtDbtInd'));
  const fallbackTxDtls = txDtlsList[0] ?? null;
  try {
    const row = buildRow({ common, amountRaw, currency, cdtDbtInd, partySource: fallbackTxDtls, rawSourceEl: ntry });
    assertValidParsedStatementRow(row);
    rows.push(row);
  } catch (err) {
    errors.push({ rawLine: serializeFragment(ntry), reason: (err as Error).message });
  }
  return { rows, errors };
}

// ─────────────────────────────────────────────────────────────────────────
// Öffentliche API
// ─────────────────────────────────────────────────────────────────────────

/**
 * Parst einen CAMT.053-XML-String (v02 oder v08) in ein `ParsedStatement`.
 *
 * **Mehrere `Stmt`/`Rpt` (BR-16 #2):** ISO erlaubt 1..n `Stmt` (bzw. `Rpt`)
 * pro Datei (Mehrtages-Sammeldatei, mehrere Konten). Früher wurde NUR das erste
 * verarbeitet und alle weiteren Ntrys still verworfen (unsichtbarer Teil-Import
 * ohne `errors[]`-Eintrag — gefährlich, weil der User „Import ok" sieht). Jetzt
 * werden die gebuchten Ntrys ALLER Statements gesammelt. `accountIban` und die
 * Salden werden aus dem LETZTEN Statement genommen — bei einer Ein-Statement-
 * Datei ist das bit-identisch zum bisherigen Verhalten (bestehende Fixtures
 * bleiben grün); bei einer Mehrtages-Sammeldatei ist der Schluss-Saldo des
 * letzten Auszugs der aussagekräftige Kontostand. (Der Import-Wizard ordnet
 * weiterhin genau ein Ziel-Konto zu; die Mehrkonten-UX bleibt separater Scope.)
 *
 * @param xmlText - Bereits dekodierter XML-Text (s. `decodeStatementFile`, BOM-frei).
 * @throws {BankImportError} `'xml_invalid'` bei DOMParser-`<parsererror>` oder
 *   fehlendem `BkToCstmrStmt`/`BkToCstmrAcctRpt`; `'no_transactions'` wenn der
 *   gefundene Wrapper kein `Stmt`- bzw. `Rpt`-Element enthält.
 */
export function parseCamt053(xmlText: string): ParsedStatement {
  const doc = new DOMParser().parseFromString(xmlText, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new BankImportError('xml_invalid', 'DOMParser meldete <parsererror> — Datei ist kein valides XML.');
  }

  const bkToCstmrStmt =
    (doc.getElementsByTagNameNS('*', 'BkToCstmrStmt')[0] as Element | undefined) ??
    (doc.getElementsByTagNameNS('*', 'BkToCstmrAcctRpt')[0] as Element | undefined);
  if (!bkToCstmrStmt) {
    throw new BankImportError(
      'xml_invalid',
      'Weder BkToCstmrStmt- noch BkToCstmrAcctRpt-Element gefunden — keine CAMT.053/CAMT.052-Struktur.'
    );
  }

  // BR-16 #2: ALLE Stmt/Rpt sammeln (nicht nur das erste). In der Praxis
  // enthält eine Datei entweder `Stmt` (camt.053) ODER `Rpt` (camt.052); das
  // Zusammenführen beider Listen ist unschädlich (die jeweils andere ist leer).
  const statements = [
    ...directChildren(bkToCstmrStmt, 'Stmt'),
    ...directChildren(bkToCstmrStmt, 'Rpt'),
  ];
  if (statements.length === 0) {
    throw new BankImportError('no_transactions', 'Kein Stmt- oder Rpt-Element im BkToCstmrStmt/BkToCstmrAcctRpt.');
  }

  const rows: ParsedStatementRow[] = [];
  const errors: Array<{ rawLine: string; reason: string }> = [];
  for (const stmt of statements) {
    const bookedEntries = directChildren(stmt, 'Ntry').filter(
      (ntry) => textOf(directChild(ntry, 'Sts')) === 'BOOK'
    );
    for (const ntry of bookedEntries) {
      const result = extractEntryRows(ntry);
      rows.push(...result.rows);
      errors.push(...result.errors);
    }
  }

  // Konto-IBAN/Salden aus dem letzten Statement (Ein-Statement-Datei: identisch
  // zum bisherigen Verhalten; Mehrtages-Sammeldatei: aussagekräftiger Schluss-Saldo).
  const lastStmt = statements[statements.length - 1];
  const accountIban = extractAccountIban(lastStmt);
  const { openingBalanceCents, closingBalanceCents, closingBalanceDate } = extractBalances(lastStmt);
  // MA-2: analog zu accountIban/Salden aus dem LETZTEN Statement (Ein-Statement-
  // Datei: einzige Quelle; Mehrtages-Sammeldatei: die Periode des Schluss-Auszugs
  // ist die für die Mahn-Automatik-Coverage relevante).
  const { coverageFrom, coverageTo } = extractCoveragePeriod(lastStmt);

  return {
    accountIban,
    openingBalanceCents,
    closingBalanceCents,
    closingBalanceDate,
    rows,
    errors,
    coverageFrom,
    coverageTo,
  };
}
